"use client";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import orderBy from "lodash/orderBy";
import { useContext, useEffect, useMemo, useState } from "react";

import RadioInput from "@stadium/ui/src/components/form/RadioInput";
import Label from "@stadium/ui/src/components/form/Label";
import Button from "@stadium/checkout/src/components/shared/buttons";
import Modal from "@stadium/ui/src/components/ModalNew";
import Portal from "@stadium/ui/src/components/portal";
import {
  ModalHeader,
  CloseButton,
  ModalBody,
  ModalContent,
  ModalDialog,
} from "@stadium/ui/src/components/ModalNew/utils";

import { apolloClient as client } from "@stadium/services/apollo-client/utils/instance";
import { getWarehouseAddressId } from "@stadium/redux-utils/selectors/configSelector";
import { useConfig } from "@stadium/hooks/lib/use-config";
import Loaders from "@stadium/ui/src/components/loaders/loaders";

import useFetchSavedAddressesList from "@stadium/checkout/src/hooks/use-fetch-save-addresses-list";
import { updateAddress } from "@stadium/checkout/src/graphql/mutations/address/update-address";
import { createAddress } from "@stadium/checkout/src/graphql/mutations/address/create-address";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import AddressForm from "@stadium/checkout/src/components/shipping-address/address-form";
import Address from "@stadium/checkout/src/components/shipping-address/address";
import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";
import { ANALYTICS_EVENT_ACTION_SELECTED } from "@stadium/services/analytics/constants/default";
import { ADDRESS_TARGET } from "@stadium/checkout/src/constants/analytics";
import { ExternalLinkButton } from "@stadium/checkout/src/components/shared/button";

import { STADIUM_WAREHOUSE_ADDRESS, weglotNoTranslationClassName } from "@stadium/constants";
import { ADDRESS_TYPE_OPTIONS } from "@stadium/checkout/src/constants/shipping";
import { GLOBAL_COUNTRY } from "@stadium/constants/country";
import { BILLING_DETAILS_STEP_STATE } from "@stadium/checkout/src/constants/order";
import { useFlags } from "@stadium/services/launch-darkly";
import { threePlWarehouseUpdatesFlag } from "@stadium/services/launch-darkly/constants";

import addressStyles from "./address.module.scss";

const { savedAddress, newAddress, warehouseAddress } = ADDRESS_TYPE_OPTIONS;
const DEFAULT_VISIBLE_ADDRESSES_COUNT = 3;

const CheckoutShippingAddress = ({
  selectedCountry,
  showWarehouseAddress = false,
  isDefaultAddressWarehouse = false,
  displayShowMore = true,
  selectedAddress,
  setSelectedAddress,
  setSubmittedAddress = () => {},
  renderCustomAddressUI,
  renderContinueCta = () => {},
  orderData,
  defaultAddressId = null,
  hideCountryFlag = false,
  eventLocationAnalytics,
  warehouseDetails = null,
}) => {
  const { isMobileDevice } = useContext(CurrentDeviceContext);

  const warehouseAddressId = useConfig(getWarehouseAddressId);

  const { [threePlWarehouseUpdatesFlag]: isThreePlWarehouseUpdatesEnabled } = useFlags();

  const warehouseAddressFromApi = warehouseDetails?.address;

  const useWarehouseIdForOrderUpdate =
    isThreePlWarehouseUpdatesEnabled && !warehouseDetails?.defaultWarehouse;

  const resolvedWarehouseAddressId = useWarehouseIdForOrderUpdate
    ? warehouseDetails?.externalId
    : warehouseAddressId;

  const warehouseAddressObj = useMemo(() => {
    if (!showWarehouseAddress) return null;

    if (isThreePlWarehouseUpdatesEnabled) {
      return {
        externalId: resolvedWarehouseAddressId,
        firstname: warehouseAddress.label,
        phone: warehouseAddressFromApi?.phone,
        address1: warehouseAddressFromApi?.address1,
        address2: warehouseAddressFromApi?.address2,
        zipcode: warehouseAddressFromApi?.zipcode,
        city: warehouseAddressFromApi?.city,
        stateNameNonUs: warehouseAddressFromApi?.stateName,
        countryName: warehouseAddressFromApi?.country?.name || warehouseAddress.subLabel,
        isWarehouseAddress: true,
      };
    }

    return {
      externalId: warehouseAddressId,
      firstname: warehouseAddress.label,
      phone: STADIUM_WAREHOUSE_ADDRESS.phoneNum,
      address1: STADIUM_WAREHOUSE_ADDRESS.line1,
      address2: STADIUM_WAREHOUSE_ADDRESS.line2,
      zipcode: STADIUM_WAREHOUSE_ADDRESS.zip,
      city: STADIUM_WAREHOUSE_ADDRESS.city,
      stateNameNonUs: STADIUM_WAREHOUSE_ADDRESS.state,
      countryName: STADIUM_WAREHOUSE_ADDRESS.countryName,
      isWarehouseAddress: true,
    };
  }, [
    showWarehouseAddress,
    resolvedWarehouseAddressId,
    warehouseAddressId,
    isThreePlWarehouseUpdatesEnabled,
    warehouseAddressFromApi,
  ]);

  const withAnalytics = useWithAnalytics();

  const isGlobalCountry = GLOBAL_COUNTRY.iso === selectedCountry?.iso;

  const {
    savedAddresses,
    refetch: onAddAddressSuccess,
    loading: addressesLoading,
  } = useFetchSavedAddressesList();

  const [addressType, setAddressType] = useState(
    isDefaultAddressWarehouse ? warehouseAddress.key : savedAddress.key,
  );

  useEffect(() => {
    if (!showWarehouseAddress || !warehouseAddressObj) return;

    const shouldSelectDefaultWarehouse =
      isDefaultAddressWarehouse &&
      addressType === warehouseAddress.key &&
      isEmpty(selectedAddress) &&
      !defaultAddressId;
    const shouldSyncWarehouseSelection = selectedAddress?.isWarehouseAddress;

    if (!shouldSelectDefaultWarehouse && !shouldSyncWarehouseSelection) return;

    setSelectedAddress(warehouseAddressObj);
    if (isDefaultAddressWarehouse) {
      setSubmittedAddress(warehouseAddressObj);
    }
  }, [
    addressType,
    isDefaultAddressWarehouse,
    defaultAddressId,
    selectedAddress?.isWarehouseAddress,
    warehouseAddressObj,
    showWarehouseAddress,
  ]);

  useEffect(() => {
    if (
      !showWarehouseAddress ||
      !selectedAddress?.isWarehouseAddress ||
      resolvedWarehouseAddressId
    ) {
      return;
    }

    setSelectedAddress({});
    setSubmittedAddress({});
  }, [resolvedWarehouseAddressId, selectedAddress?.isWarehouseAddress, showWarehouseAddress]);

  const [visibleAddressesCount, setVisibleAddressesCount] = useState(
    DEFAULT_VISIBLE_ADDRESSES_COUNT,
  );

  const {
    id: countryId,
    international: isInternational,
    iso: countryIso,
    name: countryName,
    dialCode: countryDialCode,
  } = selectedCountry;

  const userAddresses = useMemo(() => {
    if (isEmpty(savedAddresses)) return [];

    const addresses = isGlobalCountry
      ? [...savedAddresses]
      : [...savedAddresses.filter((country) => parseInt(country.countryId) === countryId)];

    const setDefaultAddress =
      isEmpty(selectedAddress) ||
      (selectedAddress?.countryId != countryId && !isDefaultAddressWarehouse);

    //move default address to top.
    if (addresses.length) {
      let defaultAddressIndex;
      // if we have a default selected address, will select that
      if (selectedAddress?.externalId) {
        defaultAddressIndex = addresses.findIndex(
          (address) => address.externalId === selectedAddress.externalId,
        );
      } else if (defaultAddressId) {
        defaultAddressIndex = addresses.findIndex(
          (address) => address.externalId === defaultAddressId,
        );
      } else {
        defaultAddressIndex = addresses.findIndex((address) => address.default);
      }

      if (defaultAddressIndex !== -1) {
        const defaultAddress = addresses[defaultAddressIndex];
        if (setDefaultAddress) {
          setSelectedAddress(defaultAddress);
          setSubmittedAddress(defaultAddress);
        }
        return orderBy(addresses, ["default"], ["desc"]);
      } else if (setDefaultAddress) {
        setSelectedAddress(addresses[0]);
        setSubmittedAddress(addresses[0]);
      }
    } else if (setDefaultAddress) {
      // Reset the selected address when changing the country if no filled address is present for the current country
      setSelectedAddress({});
    }
    return addresses;
  }, [savedAddresses, countryId, isDefaultAddressWarehouse]);

  const noUserAddresses = useMemo(
    () =>
      !addressesLoading &&
      isEmpty(userAddresses) &&
      !(resolvedWarehouseAddressId && showWarehouseAddress),
    [userAddresses, addressesLoading, resolvedWarehouseAddressId, showWarehouseAddress],
  );

  useEffect(() => {
    if (!addressesLoading && noUserAddresses) {
      setAddressType(newAddress.key);
    }
  }, [addressesLoading, noUserAddresses]);

  const hasAddressSelected = Object.keys(selectedAddress).length > 0;
  const newAddressSelected = newAddress.key === addressType;
  const showNewAddressRadio = showWarehouseAddress || !noUserAddresses;
  const newAddressSelectedDefault =
    !addressesLoading && (newAddressSelected || !showNewAddressRadio);

  const isWarehouseAddressSelected =
    isThreePlWarehouseUpdatesEnabled && selectedAddress?.isWarehouseAddress;

  const isWarehouseCheckoutBlocked = isWarehouseAddressSelected && !resolvedWarehouseAddressId;

  const addressUpdatePayload = useMemo(() => {
    if (isEmpty(selectedAddress)) return {};

    if (isWarehouseAddressSelected) {
      if (useWarehouseIdForOrderUpdate) {
        if (!resolvedWarehouseAddressId || orderData?.warehouseId === resolvedWarehouseAddressId) {
          return {};
        }

        return {
          warehouseId: resolvedWarehouseAddressId,
          computeTax: true,
        };
      }

      if (orderData?.shipAddressId === warehouseAddressId) {
        return {};
      }

      return {
        shipAddressId: warehouseAddressId,
        computeTax: true,
      };
    }

    if (orderData?.shipAddressId === selectedAddress?.externalId) {
      return {};
    }

    return {
      shipAddressId: selectedAddress?.externalId,
      computeTax: true,
    };
  }, [
    isWarehouseAddressSelected,
    orderData?.shipAddressId,
    orderData?.warehouseId,
    resolvedWarehouseAddressId,
    selectedAddress,
    useWarehouseIdForOrderUpdate,
    warehouseAddressId,
  ]);

  const onAddAddress = async (payload, onError) => {
    const isUpdating = !!selectedAddress?.externalId;

    const mutation = isUpdating ? updateAddress : createAddress;
    let input = { address: payload };

    if (isUpdating) {
      input = {
        address: payload,
        id: selectedAddress.externalId,
      };
    }

    const { data } = await client.mutate({
      mutation,
      variables: { input },
    });

    const key = isUpdating ? "updateAddress" : "createAddress";
    const success = get(data, `${key}.success`);
    const errors = get(data, `${key}.errors`, {});
    const address = get(data, `${key}.address`);

    if (success) {
      setAddressType(savedAddress.key);
      setSelectedAddress(address);
      onAddAddressSuccess();
      setVisibleAddressesCount(DEFAULT_VISIBLE_ADDRESSES_COUNT);
    } else {
      onError(errors);
    }
  };

  const handleEditAddress = (address) => {
    setSelectedAddress(address);
    setAddressType(newAddress.key);
  };

  const warehouseSummaryLabel = useMemo(
    () => [warehouseAddressObj?.city, warehouseAddressObj?.countryName].filter(Boolean).join(", "),
    [warehouseAddressObj?.city, warehouseAddressObj?.countryName],
  );

  const renderWarehouseLockerInfo = () => (
    <div className={addressStyles.radioSubHead}>
      <div>
        {warehouseAddress.lockerLabel}
        <ExternalLinkButton
          href={warehouseAddress.learnMoreLink}
          btnStyle="link"
          className={addressStyles.radioSubHeadLink}
        >
          Learn More
        </ExternalLinkButton>
      </div>
    </div>
  );

  const renderWarehouseAddress = () => {
    if (!showWarehouseAddress || !warehouseAddressObj || !resolvedWarehouseAddressId) return null;

    return (
      <>
        <RadioInput
          value={warehouseAddressObj.externalId}
          id={warehouseAddress.key}
          checked={warehouseAddressObj?.externalId === selectedAddress?.externalId}
          name="address-type"
          onClick={() => {
            setSelectedAddress(warehouseAddressObj);
            withAnalytics(undefined, {
              ...eventLocationAnalytics,
              track: true,
              target: eventLocationAnalytics?.target ?? ADDRESS_TARGET,
              action: eventLocationAnalytics?.action ?? ANALYTICS_EVENT_ACTION_SELECTED,
            })();
            setAddressType(warehouseAddress.key);
          }}
          label={
            <Label
              iconType="radioOutline"
              styles={addressStyles}
              className={addressStyles.inputLabel}
            >
              <div
                className={`${addressStyles.labelControl} ${addressStyles.warehouseLabelControl}`}
              >
                <div className={addressStyles.labelInfo}>
                  <div className={addressStyles.wareHouseLabel}>
                    {warehouseAddress.label}
                    <span className={addressStyles.wareHouseSubLabel}>
                      {warehouseSummaryLabel || warehouseAddress.subLabel}
                    </span>
                  </div>
                  {renderWarehouseLockerInfo()}
                </div>
              </div>
            </Label>
          }
          classes={`${addressStyles.paymentOptoin} ${weglotNoTranslationClassName}`}
        />
        <hr className={addressStyles.divider} />
      </>
    );
  };

  const renderSavedAddresses = () => {
    return (
      <>
        {renderWarehouseAddress()}
        {(displayShowMore ? userAddresses.slice(0, visibleAddressesCount) : userAddresses).map(
          (address, index, addresses) => {
            const { externalId } = address;
            const isActive = selectedAddress.externalId === externalId;
            return (
              <RadioInput
                key={externalId}
                id={externalId}
                name="address"
                checked={isActive}
                classes={`${addressStyles.addressBlock} ${weglotNoTranslationClassName}`}
                onClick={() => {
                  withAnalytics(undefined, {
                    ...eventLocationAnalytics,
                    track: true,
                    target: eventLocationAnalytics?.target ?? ADDRESS_TARGET,
                    action: eventLocationAnalytics?.action ?? ANALYTICS_EVENT_ACTION_SELECTED,
                  })();
                  setAddressType(savedAddress.key);
                  setSelectedAddress(address);
                }}
                label={
                  <Label
                    iconType="radioOutline"
                    styles={addressStyles}
                    className={addressStyles.addressLabel}
                  >
                    <Address address={address} onEditAddress={handleEditAddress} />
                  </Label>
                }
              />
            );
          },
        )}
        {visibleAddressesCount < userAddresses.length && (
          <div className={addressStyles.showMoreBlock}>
            <Button
              styleType="link"
              onClick={() => setVisibleAddressesCount(userAddresses.length)}
              className={addressStyles.showMoreBtn}
            >
              Show More
            </Button>
          </div>
        )}
      </>
    );
  };

  const renderAddressForm = ({ renderCustomAddressForm } = {}) => {
    return (
      <AddressForm
        addressObj={hasAddressSelected && addressType !== warehouseAddress.key && selectedAddress}
        onAddAddress={onAddAddress}
        countryId={countryId}
        countryIso={countryIso}
        countryName={countryName}
        countryDialCode={countryDialCode}
        isInternational={isInternational}
        isCountryEditable={isGlobalCountry}
        defaultCountry={selectedCountry}
        renderCustomAddressForm={renderCustomAddressForm}
        hideCountryFlag={hideCountryFlag}
        eventLocationAnalytics={{ track: true }}
      />
    );
  };

  const renderNewAddress = () =>
    isMobileDevice && newAddressSelected ? (
      renderAddressModal()
    ) : (
      <>
        <Button
          styleType="link"
          className={addressStyles.addAddressButton}
          onClick={() => !noUserAddresses && setAddressType(savedAddress.key)}
        >
          <span className={addressStyles.minusIcon} />
          {hasAddressSelected && newAddressSelected ? "Edit Address" : "Add New Address"}
        </Button>
        {renderAddressForm()}
      </>
    );

  const renderAddressModal = () => {
    const closeModal = () => setAddressType(savedAddress.key);

    return (
      <Portal id="modal-root" dataContainerId="address-form-modal">
        <Modal
          closeModal={closeModal}
          className={`${addressStyles.primaryModal} ${addressStyles.editAddressModal}`}
        >
          <ModalDialog className={addressStyles.modalDialog}>
            <ModalContent
              className={`${addressStyles.primaryModalContent} ${addressStyles.addressModalContent} ${addressStyles.modalMobileBottomContent}`}
            >
              <ModalHeader className={addressStyles.header}>
                <div className={addressStyles.headerInfo}>
                  <div className={addressStyles.modalHeading}>
                    {hasAddressSelected && newAddressSelected ? "Edit Address" : "Add New Address"}
                  </div>
                </div>
                <CloseButton onClick={closeModal} className={`${addressStyles.closeIcon}`} />
              </ModalHeader>
              <ModalBody className={addressStyles.modalBody}>{renderAddressForm()}</ModalBody>
            </ModalContent>
          </ModalDialog>
        </Modal>
      </Portal>
    );
  };

  const renderNewAddressOption = () =>
    !newAddressSelected && (
      <span className="add-address-block">
        <Button
          styleType="link"
          className={addressStyles.addAddressButton}
          onClick={() => {
            newAddress.key !== addressType && setSelectedAddress({});
            setAddressType(newAddress.key);
          }}
        >
          <span className={addressStyles.addAddressIcon} />
          Add New Address
        </Button>
      </span>
    );

  if (typeof renderCustomAddressUI === "function") {
    return renderCustomAddressUI({
      renderSavedAddresses,
      showAddressForm: newAddressSelectedDefault,
      hideAddressForm: () => setAddressType(savedAddress.key),
      renderAddressForm,
      renderNewAddressOption,
    });
  }
  return (
    <div>
      <div className={addressStyles.addressContainer}>
        <div
          className={`${addressStyles.headingBlock} ${addressStyles.openHeading} ${weglotNoTranslationClassName}`}
        >
          <div className={addressStyles.headingContent}>
            <i className={`icon ${addressStyles.shipIcon}`} /> Shipping
          </div>
        </div>
        {addressesLoading && <Loaders.PageLoader wrapperClass="pageLoaderwrap min-h-363" />}
        {renderSavedAddresses()}
        {renderNewAddressOption()}
        {newAddressSelected && renderNewAddress()}
        <div className={addressStyles.continueActionBlock}>
          {renderContinueCta({
            isDisabled: isEmpty(selectedAddress) || isWarehouseCheckoutBlocked,
            payload: {
              ...(orderData?.countryId !== +selectedAddress?.countryId && {
                countryId: selectedAddress?.countryId,
              }),
              ...addressUpdatePayload,
            },
            stepToAutoEdit:
              selectedAddress?.externalId === resolvedWarehouseAddressId
                ? BILLING_DETAILS_STEP_STATE
                : "",
          })}
        </div>
      </div>
    </div>
  );
};

export default CheckoutShippingAddress;
